import { useNode } from "@craftjs/core";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateAudioStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { colors } from "../../../../utils";
import CustomTabs from "../../Shared/CustomTabs";
import MediaSelection from "../../Shared/MediaSelection";
import Layout from "./Layout";
import List from "./List";
import {
  MEDIA_RECORDER_ENABLED,
  AUDIO_LINK_EMBED_ENABLED,
} from "../../../../constants/featureFlags";
import { canRecordMedia } from "../../../MediaCapture";

const AudioSettings = () => {
  const {
    actions: { setProp },
    audios,
    textColor,
    waveColor,
    backgroundColor,
    iconColor,
    margin,
    allSideMargin,
    isApplyMarginOnAllSides,
  } = useNode((node) => ({
    audios: node.data.props.audios,
    textColor: node.data.props.textColor,
    waveColor: node.data.props.waveColor,
    iconColor: node.data.props.iconColor,
    backgroundColor: node.data.props.backgroundColor,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
  }));

  const dispatch = useDispatch();
  const { audio: audioProps } = useSelector((state) => state.codeblockStyles);

  const [selectedTab, setSelectedTab] = useState("0");

  // Update default props in reducer when settings change
  const updateDefaultAudioProps = (updatedProps) => {
    const updatedAudioProps = {
      ...audioProps,
      ...updatedProps,
    };
    dispatch(updateAudioStyles(updatedAudioProps));
  };

  const handleChange = (type, value, side) => {
    setProp((props) => {
      switch (type) {
        case "audio":
          const existingIndex = props.audios.findIndex(
            (doc) => doc.id === value.id
          );

          if (existingIndex !== -1) {
            props.audios[existingIndex] = {
              ...props.audios[existingIndex],
              ...value,
            };
          } else {
            props.audios.push(value);
          }
          // Don't update default for individual audios
          break;
        case "newAudios":
          props.audios = value;
          // Don't update default for audio list
          break;
        case "backgroundColor":
          props.backgroundColor = value;
          updateDefaultAudioProps({ backgroundColor: value });
          break;
        case "textColor":
          props.textColor = value;
          updateDefaultAudioProps({ textColor: value });
          break;
        case "iconColor":
          props.iconColor = value;
          updateDefaultAudioProps({ iconColor: value });
          break;
        case "waveColor":
          props.waveColor = value;
          updateDefaultAudioProps({ waveColor: value });
          break;
        case "resetBackgroundColor":
          props.backgroundColor = null;
          updateDefaultAudioProps({ backgroundColor: null });
          break;
        case "resetTextColor":
          props.textColor = colors.black;
          updateDefaultAudioProps({ textColor: colors.black });
          break;
        case "resetIconColor":
          props.iconColor = colors.black;
          updateDefaultAudioProps({ iconColor: colors.black });
          break;
        case "resetWaveColor":
          props.waveColor = colors.black;
          updateDefaultAudioProps({ waveColor: colors.black });
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
            const updatedMargin = { ...props.margin };
            updateDefaultAudioProps({ margin: updatedMargin });
          }
          break;
        case "marginToggle":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          updateDefaultAudioProps({
            isApplyMarginOnAllSides: !props.isApplyMarginOnAllSides,
          });
          break;
        case "marginChange":
          props.allSideMargin = value;
          updateDefaultAudioProps({ allSideMargin: value });
          break;
      }
    }, 500);
  };

  const handleTabChange = (e, newVal) => {
    setSelectedTab(newVal);
  };

  // Deck (audio-block-redesign) improvement #2 — Conditional Layout tab.
  // Layout's colour controls (Background/Text/Waveform/Icon) only affect an
  // in-app play bar; on an embed (Spotify/SoundCloud iframe) they do nothing —
  // the block itself prints "Layout options do not apply to 'Embed Code'". So
  // when EVERY track in the block is an embed, drop the Layout tab entirely
  // instead of rendering dead/disabled controls. A mixed block (any uploaded
  // track) keeps Layout, since those colours still apply to the uploaded bars.
  // A "Link" item that resolved to a provider iframe (embedUrl) is just as
  // colorless as an old raw "Embed Code" item -- the Layout colours only ever
  // reach OUR OWN player. A "Link" item that resolved to a podcast audioSrc
  // plays through that same on-brand player, so it keeps Layout.
  const isColorless = (a) => Boolean(a.embed) || Boolean(a.link && a.embedUrl);
  const allEmbeds = audios.length > 0 && audios.every(isColorless);

  // If Layout was the open tab and it just disappeared (block became all-embed),
  // fall back to the List tab so the pane never renders an empty/selected-but-
  // gone tab.
  useEffect(() => {
    if (allEmbeds && selectedTab === "2") {
      setSelectedTab("0");
    }
  }, [allEmbeds, selectedTab]);

  const tabOptions = new Map([
    [
      "0",
      {
        label: `List ${audios.length ? "(" + audios.length + ")" : ""}`,
        component: (
          <List
            audios={audios}
            setAudios={(audios) => handleChange("newAudios", audios)}
            onClickAdd={() => setSelectedTab("1")}
            iconColor={iconColor}
          />
        ),
      },
    ],
    [
      "1",
      {
        label: "Add Audio",
        component: (
          <MediaSelection
            type="audio"
            handleChange={handleChange}
            media={audios}
            setMedia={(audios) => handleChange("newAudios", audios)}
            categories={[
              // Gordon decision 2026-09-15 00:10 PT (Option A): "Link" is the
              // first tab, replacing "Embed Code", when the flag is ON.
              ...(AUDIO_LINK_EMBED_ENABLED ? ["Link"] : []),
              "Upload",
              ...(MEDIA_RECORDER_ENABLED && canRecordMedia("audio")
                ? ["Record"]
                : []),
              ...(AUDIO_LINK_EMBED_ENABLED ? [] : ["Embed Code"]),
              "Recent",
            ]}
          />
        ),
      },
    ],
  ]);

  if (!allEmbeds) {
    tabOptions.set("2", {
      label: "Layout",
      component: (
        <Layout
          handleChange={handleChange}
          backgroundColor={backgroundColor}
          waveColor={waveColor}
          iconColor={iconColor}
          textColor={textColor}
          margin={margin}
          handleMarginChange={(side, value) =>
            handleChange("margin", value, side)
          }
          handleSliderMarginChange={(value) =>
            handleChange("marginChange", value, null)
          }
          handleMarginCheckBox={() => handleChange("marginToggle")}
          allSideMargin={allSideMargin}
          isApplyMarginOnAllSides={isApplyMarginOnAllSides}
        />
      ),
      disabled: audios.length == 0,
    });
  }

  return (
    <>
      <CustomTabs
        selectedTab={selectedTab}
        handleTabChange={handleTabChange}
        options={tabOptions}
      />
    </>
  );
};

export default AudioSettings;
